import { AbstractControl, ValidationErrors, ValidatorFn } from '@angular/forms';
import { ApiService } from '@core/api.service';
import { Subscription } from 'rxjs';

export const PASSWORD_POLICY_CODES = {
  MIN_LEN: 'PASS_MIN_LEN_10',
  TOO_COMMON: 'PASS_TOO_COMMON',
  COMPLEXITY: 'PASS_COMPLEXITY_REQUIRED',
  MATCHES_USERNAME: 'PASS_MATCHES_USERNAME',
};

export class PasswordPolicyState {
  tooCommon = false;
  complexityInvalid = false;
  matchesUsername = false;
  loading = false;
  lastCheckedKey = '';

  clearFlags(): void {
    this.tooCommon = false;
    this.complexityInvalid = false;
    this.matchesUsername = false;
  }

  applyMessages(messages: string[] = []): void {
    const list = messages || [];
    this.matchesUsername = list.indexOf(PASSWORD_POLICY_CODES.MATCHES_USERNAME) >= 0;
    this.complexityInvalid = list.indexOf(PASSWORD_POLICY_CODES.COMPLEXITY) >= 0;
    this.tooCommon = list.indexOf(PASSWORD_POLICY_CODES.TOO_COMMON) >= 0;
  }

  toControlErrors(): ValidationErrors | null {
    const errors: ValidationErrors = {};
    if (this.tooCommon) {
      errors[PASSWORD_POLICY_CODES.TOO_COMMON] = true;
    }
    if (this.complexityInvalid) {
      errors[PASSWORD_POLICY_CODES.COMPLEXITY] = true;
    }
    if (this.matchesUsername) {
      errors[PASSWORD_POLICY_CODES.MATCHES_USERNAME] = true;
    }
    return Object.keys(errors).length ? errors : null;
  }
}

export class PasswordPolicyHelper {
  static shortPassword(control: AbstractControl): ValidationErrors | null {
    const password = control.value;
    if (password && password.length < 10) {
      control.markAsTouched();
      return { [PASSWORD_POLICY_CODES.MIN_LEN]: true };
    }
    return null;
  }

  static commonPasswordValidator(state: PasswordPolicyState): ValidatorFn {
    return () => state.toControlErrors();
  }

  static matchPasswords(passwordKey: string, confirmKey: string): ValidatorFn {
    return (group: AbstractControl): ValidationErrors | null => {
      const passwordControl = group.get(passwordKey);
      const confirmControl = group.get(confirmKey);
      if (!passwordControl || !confirmControl) {
        return null;
      }

      const password = passwordControl.value;
      const confirmPassword = confirmControl.value;
      if (confirmPassword === null) {
        return null;
      }

      if (password !== confirmPassword) {
        confirmControl.setErrors({ MatchPassword: true });
        confirmControl.markAsTouched({ onlySelf: true });
      } else {
        const errors = confirmControl.errors;
        if (errors) {
          delete errors.MatchPassword;
          confirmControl.setErrors(Object.keys(errors).length ? errors : null);
        } else {
          confirmControl.setErrors(null);
        }
      }
      return null;
    };
  }

  static collectForbiddenWords(values: Array<string | number | null | undefined>): string[] {
    return (values || [])
      .filter(value => value != null && String(value).trim().length > 0)
      .map(value => String(value).trim());
  }

  static flattenMessages(value: any): string[] {
    if (!value) {
      return [];
    }
    if (typeof value === 'string') {
      return [value];
    }
    if (Array.isArray(value)) {
      return value.reduce((acc: string[], item) => acc.concat(PasswordPolicyHelper.flattenMessages(item)), []);
    }
    return [];
  }

  static extractApiMessages(error: any): string[] {
    if (!error) {
      return [];
    }
    if (error.error && error.error.new_password1) {
      return PasswordPolicyHelper.flattenMessages(error.error.new_password1);
    }
    if (error.error && error.error.password) {
      return PasswordPolicyHelper.flattenMessages(error.error.password);
    }
    if (error.error && error.error.non_field_errors) {
      return PasswordPolicyHelper.flattenMessages(error.error.non_field_errors);
    }
    if (typeof error === 'string') {
      return [error];
    }
    return [];
  }

  static controlHasPolicyError(control: AbstractControl | null): boolean {
    if (!control) {
      return false;
    }
    return !!(
      control.hasError(PASSWORD_POLICY_CODES.MIN_LEN) ||
      control.hasError(PASSWORD_POLICY_CODES.TOO_COMMON) ||
      control.hasError(PASSWORD_POLICY_CODES.COMPLEXITY) ||
      control.hasError(PASSWORD_POLICY_CODES.MATCHES_USERNAME)
    );
  }

  /**
   * Live-check password against backend policy.
   * Returns the active subscription (existing or new), or null when cleared/skipped.
   */
  static checkPassword(
    apiService: ApiService,
    password: string,
    forbiddenWords: string[],
    state: PasswordPolicyState,
    passwordControl: AbstractControl,
    onDone?: () => void,
    previousSub?: Subscription | null,
  ): Subscription | null {
    if (!password || password.length < 10) {
      if (previousSub) {
        previousSub.unsubscribe();
      }
      state.clearFlags();
      state.lastCheckedKey = '';
      state.loading = false;
      passwordControl.updateValueAndValidity({ emitEvent: false });
      if (onDone) {
        onDone();
      }
      return null;
    }

    const words = forbiddenWords || [];
    const checkKey = `${password}|${words.join('|')}`;
    if (checkKey === state.lastCheckedKey) {
      return previousSub || null;
    }

    if (previousSub) {
      previousSub.unsubscribe();
    }

    state.lastCheckedKey = checkKey;
    state.loading = true;
    if (onDone) {
      onDone();
    }

    return apiService.post('/auth/validate_pass/', {
      password,
      forbidden_words: words,
    }).subscribe(
      (res: string[]) => {
        state.loading = false;
        state.applyMessages(res);
        passwordControl.updateValueAndValidity({ emitEvent: false });
        if (onDone) {
          onDone();
        }
      },
      () => {
        state.loading = false;
        if (onDone) {
          onDone();
        }
      }
    );
  }
}
